// src/pages/user/StaffPage.jsx
import { useEffect, useMemo, useState } from "react";
import {
  UserPlus,
  Users,
  RefreshCw,
  Mail,
  RotateCw,
  Shield,
  X,
  Settings as SettingsIcon,
  ArrowRight,
} from "lucide-react";
import toast from "react-hot-toast";
import { injectUserStyles } from "../userStyles";

const STYLE_ID = "staff-page-styles";
function injectLocal() {
  injectUserStyles();
  if (document.getElementById(STYLE_ID)) return;
  const el = document.createElement("style");
  el.id = STYLE_ID;
  el.textContent = `
    .sf-table-card { background:#fff; border:1px solid #e8ecf0; border-radius:10px; overflow:hidden; }
    .sf-table { width:100%; border-collapse:collapse; font-size:13px; }
    .sf-table thead tr { background:#f9fafb; border-bottom:1px solid #e8ecf0; }
    .sf-table th {
      padding:10px 16px; text-align:left;
      font-family:'DM Sans',sans-serif; font-size:9.5px; letter-spacing:.04em;
      color:#9ca3af; white-space:nowrap;
    }
    .sf-table td { padding:13px 16px; vertical-align:middle; border-bottom:1px solid #f3f4f6; }
    .sf-table tbody tr:last-child td { border-bottom:none; }
    .sf-table tbody tr:hover td { background:#fafafa; }
    .sf-member-name { font-size:13.5px; font-weight:500; color:#0c1118; }
    .sf-member-email { font-size:11.5px; color:#9ca3af; margin-top:1px; }
    .sf-role-badge {
      display:inline-flex; padding:3px 9px; border-radius:5px;
      font-family:'DM Sans',sans-serif; font-size:10px; letter-spacing:.06em; font-weight:500;
    }
    .sf-role-owner  { background:rgba(249,171,20,.12); color:#92400e; }
    .sf-role-staff  { background:rgba(29,158,117,.1);  color:#065f46; }
    .sf-role-member { background:rgba(99,102,241,.1);  color:#3730a3; }
    .sf-role-admin  { background:rgba(239,68,68,.1);   color:#991b1b; }
    .sf-modal-backdrop {
      position:fixed; inset:0; z-index:50;
      background:rgba(0,0,0,.6); backdrop-filter:blur(4px);
      display:flex; align-items:center; justify-content:center; padding:20px;
    }
    .sf-modal {
      background:#fff; border-radius:12px; overflow:hidden;
      width:100%; max-width:440px;
      box-shadow:0 20px 60px rgba(0,0,0,.2);
    }
    .sf-modal-head {
      padding:18px 22px 14px; border-bottom:1px solid #f3f4f6;
    }
    .sf-modal-title { font-size:15px; font-weight:600; color:#0c1118; }
    .sf-modal-sub   { font-size:12px; color:#9ca3af; margin-top:2px; }
    .sf-modal-body  { padding:20px 22px; }
    .sf-modal-foot  { padding:14px 22px; border-top:1px solid #f3f4f6; display:flex; justify-content:flex-end; gap:10px; }
    .sf-perm-row {
      display:flex; align-items:center; justify-content:space-between;
      padding:10px 0; border-bottom:1px solid #f9fafb;
    }
    .sf-perm-row:last-child { border-bottom:none; }
    .sf-perm-label { font-size:13px; color:#374151; }
    .sf-toggle {
      position:relative; width:36px; height:20px;
      border-radius:10px; cursor:pointer; transition:background 200ms;
    }
    .sf-toggle-on  { background:#10b981; }
    .sf-toggle-off { background:#d1d5db; }
    .sf-toggle-knob {
      position:absolute; top:2px; width:16px; height:16px;
      border-radius:50%; background:#fff; box-shadow:0 1px 3px rgba(0,0,0,.2);
      transition:left 200ms;
    }
    .sf-toggle-on .sf-toggle-knob  { left:18px; }
    .sf-toggle-off .sf-toggle-knob { left:2px; }
    .sf-invite-form {
      display:flex; gap:10px; flex-wrap:wrap;
    }
    .sf-invite-input {
      flex:1; min-width:200px; padding:9px 12px;
      border:1px solid #e2e8f0; border-radius:7px; font-size:13px;
      font-family:'DM Sans',sans-serif; outline:none;
      transition:border-color 150ms;
    }
    .sf-invite-input:focus { border-color:#f9ab14; }
    .sf-invite-select {
      padding:9px 12px; border:1px solid #e2e8f0; border-radius:7px;
      font-size:13px; font-family:'DM Sans',sans-serif; background:#fff; outline:none;
      transition:border-color 150ms;
    }
    .sf-invite-select:focus { border-color:#f9ab14; }
    .sf-empty-row { padding:40px 20px; text-align:center; }
  `;
  document.head.appendChild(el);
}

/* ── Constants ── */
const BASE = process.env.REACT_APP_BACKEND_URL || "";

async function jfetch(path, opts = {}) {
  const res = await fetch(`${BASE}${path}`, {
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    ...opts,
  });
  if (!res.ok) {
    let msg = "Request failed";
    try {
      const t = await res.text();
      msg = t || msg;
    } catch {}
    throw new Error(msg);
  }
  return res.json();
}

const ROLE_LABELS = {
  owner: "Owner",
  admin: "Admin",
  staff: "Staff",
  member: "Member",
};

const STAFF_ROUTE_OPTIONS = [
  { key: "app.orgquotes", label: "Quotes" },
  { key: "app.quoteeditor", label: "Quote Editor" },
  { key: "app.autoestimator", label: "Auto Estimator" },
  { key: "app.branding.edit", label: "Custom Branding" },
  { key: "app.subscription", label: "Subscription" },
];

/* ── Permission Modal ── */
// target shape:
//   { kind: "member",  userId, email }
//   { kind: "invite",  inviteId, email }
function StaffPermsModal({ open, onClose, target }) {
  const [loading, setLoading] = useState(true);
  const [allow, setAllow] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let ignore = false;
    async function run() {
      if (!open || !target) return;
      setLoading(true);
      try {
        const url =
          target.kind === "invite"
            ? `/api/orgs/primary/invites/${target.inviteId}/perms`
            : `/api/orgs/primary/members/${target.userId}/perms`;
        const data = await jfetch(url);
        if (!ignore) setAllow(data.allow ?? []);
      } catch {
        if (!ignore) setAllow([]);
      } finally {
        if (!ignore) setLoading(false);
      }
    }
    run();
    return () => {
      ignore = true;
    };
  }, [open, target]);

  async function save() {
    setSaving(true);
    try {
      const url =
        target.kind === "invite"
          ? `/api/orgs/primary/invites/${target.inviteId}/perms`
          : `/api/orgs/primary/members/${target.userId}/perms`;
      await jfetch(url, { method: "PUT", body: JSON.stringify({ allow }) });
      toast.success("Permissions updated");
      onClose();
    } catch (e) {
      toast.error(e?.message || "Failed to save permissions");
    } finally {
      setSaving(false);
    }
  }

  if (!open || !target) return null;
  return (
    <div className="sf-modal-backdrop" onClick={onClose}>
      <div className="sf-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sf-modal-head">
          <div className="sf-modal-title">
            {target.kind === "invite"
              ? "Pre-set Permissions"
              : "Access Permissions"}
          </div>
          <div className="sf-modal-sub">
            {target.email}
            {target.kind === "invite" && (
              <span style={{ marginLeft: 6, fontSize: 11, color: "#f9ab14" }}>
                · Pending invite
              </span>
            )}
          </div>
        </div>
        <div className="sf-modal-body">
          {loading ? (
            <div className="up-loading">
              <div className="up-spinner" /> Loading…
            </div>
          ) : (
            STAFF_ROUTE_OPTIONS.map(({ key, label }) => {
              const enabled = allow.includes(key);
              return (
                <div className="sf-perm-row" key={key}>
                  <span className="sf-perm-label">{label}</span>
                  <div
                    className={`sf-toggle ${enabled ? "sf-toggle-on" : "sf-toggle-off"}`}
                    onClick={() =>
                      setAllow((prev) =>
                        enabled
                          ? prev.filter((k) => k !== key)
                          : [...prev, key],
                      )
                    }
                  >
                    <div className="sf-toggle-knob" />
                  </div>
                </div>
              );
            })
          )}
        </div>
        <div className="sf-modal-foot">
          <button className="up-btn up-btn-outline" onClick={onClose}>
            Cancel
          </button>
          <button
            className="up-btn up-btn-dark"
            onClick={save}
            disabled={saving || loading}
          >
            {saving ? "Saving…" : "Save Permissions"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ── Main ── */
export default function StaffPage() {
  useEffect(() => {
    injectLocal();
  }, []);

  const [members, setMembers] = useState([]);
  const [invites, setInvites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [orgId, setOrgId] = useState(null);
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [inviting, setInviting] = useState(false);
  const [permsTarget, setPermsTarget] = useState(null);
  const [search, setSearch] = useState("");

  async function load() {
    setLoading(true);
    try {
      const [mRes, iRes] = await Promise.all([
        jfetch("/api/orgs/primary/members"),
        jfetch("/api/orgs/primary/invites"),
      ]);
      setMembers(mRes.items || []);
      setInvites(iRes.items || []);
      setOrgId(mRes.orgId || null);
    } catch (e) {
      toast.error(e?.message || "Failed to load staff");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, []);

  async function invite() {
    if (!email.trim()) return;
    setInviting(true);
    try {
      await jfetch("/api/orgs/primary/invites", {
        method: "POST",
        body: JSON.stringify({ email: email.trim(), role }),
      });
      toast.success(`Invite sent to ${email.trim()}`);
      setEmail("");
      load();
    } catch (e) {
      toast.error(e?.message || "Failed to send invite");
    } finally {
      setInviting(false);
    }
  }

  async function resendInvite(inviteId) {
    try {
      await jfetch(`/api/orgs/primary/invites/${inviteId}/resend`, {
        method: "POST",
      });
      toast.success("Invite resent");
    } catch (e) {
      toast.error(e?.message || "Failed to resend invite");
    }
  }

  async function revokeInvite(inviteId) {
    try {
      await jfetch(`/api/orgs/primary/invites/${inviteId}`, {
        method: "DELETE",
      });
      toast.success("Invite revoked");
      load();
    } catch (e) {
      toast.error(e?.message || "Failed to revoke invite");
    }
  }

  async function removeMember(userId) {
    try {
      await jfetch(`/api/orgs/primary/members/${userId}`, { method: "DELETE" });
      toast.success("Member removed");
      load();
    } catch (e) {
      toast.error(e?.message || "Failed to remove member");
    }
  }

  async function changeRole(userId, newRole) {
    try {
      await jfetch(`/api/orgs/primary/members/${userId}/role`, {
        method: "PATCH",
        body: JSON.stringify({ role: newRole }),
      });
      toast.success(`Role updated to ${newRole}`);
      load();
    } catch (e) {
      toast.error(e?.message || "Failed to update role");
    }
  }

  function roleBadgeClass(r) {
    const map = {
      owner: "sf-role-owner",
      staff: "sf-role-staff",
      member: "sf-role-member",
      admin: "sf-role-admin",
    };
    return `sf-role-badge ${map[(r || "").toLowerCase()] || ""}`;
  }

  const filteredMembers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return members;
    return members.filter(
      (m) =>
        (m.email || "").toLowerCase().includes(term) ||
        (m.firstname || "").toLowerCase().includes(term) ||
        (m.lastname || "").toLowerCase().includes(term),
    );
  }, [members, search]);

  const KPIS = [
    { accent: "#0c1118", val: members.length, lbl: "Total Members" },
    { accent: "#f9ab14", val: invites.length, lbl: "Pending Invites" },
    {
      accent: "#1d9e75",
      val: members.filter((m) => (m.role || "").toLowerCase() === "staff")
        .length,
      lbl: "Staff Members",
    },
  ];

  return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Header */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: "12px",
          marginBottom: "24px",
        }}
      >
        <div>
          <div className="up-title">Team Management</div>
          <div className="up-sub">
            Manage your staff, members and invitations
          </div>
        </div>
        <button
          className="up-btn up-btn-outline"
          onClick={load}
          disabled={loading}
        >
          <RefreshCw
            size={14}
            style={{
              animation: loading ? "upSpin .7s linear infinite" : "none",
            }}
          />
          Refresh
        </button>
      </div>

      {/* KPIs */}
      <div
        className="up-kpi-grid"
        style={{ gridTemplateColumns: "repeat(3,1fr)" }}
      >
        {KPIS.map((k) => (
          <div className="up-kpi" key={k.lbl}>
            <div className="up-kpi-accent" style={{ background: k.accent }} />
            <div className="up-kpi-val">{loading ? "—" : k.val}</div>
            <div className="up-kpi-lbl">{k.lbl}</div>
          </div>
        ))}
      </div>

      {/* Invite form */}
      <div className="up-panel" style={{ marginBottom: "20px" }}>
        <div className="up-panel-head">
          <div className="up-panel-title">
            <UserPlus size={15} style={{ color: "#f9ab14" }} /> Invite Team
            Member
          </div>
        </div>
        <div style={{ padding: "16px 20px" }}>
          <div className="sf-invite-form">
            <input
              className="sf-invite-input"
              type="email"
              placeholder="Email address"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && invite()}
            />
            <select
              className="sf-invite-select"
              value={role}
              onChange={(e) => setRole(e.target.value)}
            >
              <option value="member">Member</option>
              <option value="staff">Staff</option>
            </select>
            <button
              className="up-btn up-btn-dark"
              onClick={invite}
              disabled={inviting || !email.trim()}
            >
              {inviting ? "Sending…" : "Send Invite"}
            </button>
          </div>
        </div>
      </div>

      {/* Members table */}
      <div className="sf-table-card" style={{ marginBottom: "20px" }}>
        <div className="up-panel-head" style={{ padding: "14px 20px" }}>
          <div className="up-panel-title">
            <Users size={15} style={{ color: "#f9ab14" }} /> Members (
            {filteredMembers.length})
          </div>
          <input
            style={{
              padding: "6px 10px",
              border: "1px solid #e2e8f0",
              borderRadius: "6px",
              fontSize: "12px",
              fontFamily: "'DM Sans',sans-serif",
              outline: "none",
              width: 180,
            }}
            placeholder="Search members…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        {loading ? (
          <div className="up-loading">
            <div className="up-spinner" /> Loading…
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="sf-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Role</th>
                  <th>Joined</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredMembers.length === 0 ? (
                  <tr>
                    <td colSpan={4}>
                      <div className="sf-empty-row">
                        <div className="up-empty-sub">No members found</div>
                      </div>
                    </td>
                  </tr>
                ) : (
                  filteredMembers.map((m) => (
                    <tr key={m.userId || m.id}>
                      <td>
                        <div className="sf-member-name">
                          {[m.firstname, m.lastname]
                            .filter(Boolean)
                            .join(" ") || m.email}
                        </div>
                        <div className="sf-member-email">{m.email}</div>
                      </td>
                      <td>
                        <span className={roleBadgeClass(m.role)}>
                          {ROLE_LABELS[m.role?.toLowerCase()] || m.role}
                        </span>
                      </td>
                      <td
                        style={{
                          fontFamily: "'DM Sans', sans-serif",
                          fontSize: "11px",
                          color: "#9ca3af",
                        }}
                      >
                        {m.joinedAt
                          ? new Date(m.joinedAt).toLocaleDateString()
                          : "—"}
                      </td>
                      <td>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                            gap: "6px",
                          }}
                        >
                          {/* Role change — only for non-owner members */}
                          {(m.role || "").toLowerCase() !== "owner" && (
                            <select
                              value={(m.role || "").toLowerCase()}
                              onChange={(e) =>
                                changeRole(m.userId || m.id, e.target.value)
                              }
                              className="sf-invite-select"
                              style={{ padding: "4px 8px", fontSize: "11.5px" }}
                            >
                              <option value="member">Member</option>
                              <option value="staff">Staff</option>
                            </select>
                          )}
                          {/* Permissions — only for Staff */}
                          {(m.role || "").toLowerCase() === "staff" && (
                            <button
                              className="up-btn up-btn-outline"
                              style={{
                                padding: "5px 10px",
                                fontSize: "11.5px",
                              }}
                              onClick={() =>
                                setPermsTarget({
                                  kind: "member",
                                  userId: m.userId || m.id,
                                  email: m.email,
                                })
                              }
                            >
                              <Shield size={12} /> Permissions
                            </button>
                          )}
                          {(m.role || "").toLowerCase() !== "owner" && (
                            <button
                              className="up-btn up-btn-outline"
                              style={{
                                padding: "5px 10px",
                                fontSize: "11.5px",
                                color: "#dc2626",
                              }}
                              onClick={() => removeMember(m.userId || m.id)}
                            >
                              <X size={12} /> Remove
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pending invites */}
      {invites.length > 0 && (
        <div className="sf-table-card">
          <div className="up-panel-head" style={{ padding: "14px 20px" }}>
            <div className="up-panel-title">
              <Mail size={15} style={{ color: "#f9ab14" }} /> Pending Invites (
              {invites.length})
            </div>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table className="sf-table">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Sent</th>
                  <th style={{ textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {invites.map((inv) => (
                  <tr key={inv.id}>
                    <td>
                      <div className="sf-member-name">{inv.email}</div>
                    </td>
                    <td>
                      <span className={roleBadgeClass(inv.role)}>
                        {ROLE_LABELS[inv.role?.toLowerCase()] || inv.role}
                      </span>
                    </td>
                    <td
                      style={{
                        fontFamily: "'DM Sans', sans-serif",
                        fontSize: "11px",
                        color: "#9ca3af",
                      }}
                    >
                      {inv.createdAt
                        ? new Date(inv.createdAt).toLocaleDateString()
                        : "—"}
                    </td>
                    <td>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: "6px",
                        }}
                      >
                        <button
                          className="up-btn up-btn-outline"
                          style={{ padding: "5px 10px", fontSize: "11.5px" }}
                          onClick={() =>
                            setPermsTarget({
                              kind: "invite",
                              inviteId: inv.id,
                              email: inv.email,
                            })
                          }
                        >
                          <Shield size={12} /> Permissions
                        </button>
                        <button
                          className="up-btn up-btn-outline"
                          style={{ padding: "5px 10px", fontSize: "11.5px" }}
                          onClick={() => resendInvite(inv.id)}
                        >
                          <RotateCw size={12} /> Resend
                        </button>
                        <button
                          className="up-btn up-btn-outline"
                          style={{
                            padding: "5px 10px",
                            fontSize: "11.5px",
                            color: "#dc2626",
                          }}
                          onClick={() => revokeInvite(inv.id)}
                        >
                          <X size={12} /> Revoke
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Permissions modal */}
      <StaffPermsModal
        open={!!permsTarget}
        onClose={() => setPermsTarget(null)}
        target={permsTarget}
      />
    </div>
  );
}
